import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { showToast } from "../../helper/generalHelper";
import { toast } from "react-toastify";
import { FiArrowLeft, FiEye, FiEyeOff } from "react-icons/fi";

const AddStaff = () => {
  const navigate = useNavigate();
  const isStaffRoute = window.location.pathname.includes("/restaurant-staff");
  const routePrefix = isStaffRoute ? "/restaurant-staff/employee" : "/restaurant";
  const apiPrefix = isStaffRoute ? "restaurant-staff" : "restaurant";

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    password: "",
    restaurantId: "", // Selected business/branch
    roleId: "",
    profileImage: null,
  });

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [data, setData] = useState({
    restaurants: [], // List of businesses and branches
    roles: [],
  });

  useEffect(() => {
    fetchRestaurantsAndRoles();
  }, []);

  const fetchRestaurantsAndRoles = async () => {
    try {
      setLoading(true);
      setError("");
      
      // Fetch both restaurants and roles
      const restaurantEndpoint = isStaffRoute ? `${apiPrefix}/restaurant/get` : `${apiPrefix}/restaurant/all`;
      const [restaurantRes, rolesRes] = await Promise.all([
        api.get(restaurantEndpoint),
        api.get(`${apiPrefix}/staff/roles`)
      ]);

      let restaurants = [];
      if (isStaffRoute) {
        // Staff route returns a single restaurant object directly in data
        if (restaurantRes.data && restaurantRes.data.id) {
          restaurants = [restaurantRes.data];
        }
      } else {
        // Admin route returns an array
        restaurants = restaurantRes.data?.reataurants || [];
      }

      const rolesData = rolesRes.data?.data || rolesRes.data;
      
      setData({
        restaurants: restaurants,
        roles: Array.isArray(rolesData.roles) ? rolesData.roles : []
      });

      // Default restaurant selection if current ID exists
      const currentResId = localStorage.getItem("current_restaurant_id");
      if (currentResId && restaurants.some(r => r.id === currentResId)) {
        setFormData(prev => ({ ...prev, restaurantId: currentResId }));
      }
      
      console.log("Restaurants:", restaurants);
      console.log("Roles:", rolesData.roles);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load restaurants and roles");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profileImage") {
      const file = files[0];
      setFormData({ ...formData, profileImage: file });
      setPreview(URL.createObjectURL(file));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName.trim()) {
      setError("First name is required");
      return;
    }
    if (!formData.mobile.trim()) {
      setError("Mobile number is required");
      return;
    }
    if (!formData.password.trim()) {
      setError("Password is required");
      return;
    }
    if (!formData.restaurantId) {
      setError("Please select a business/branch");
      return;
    }
    if (!formData.roleId) {
      setError("Please select a role");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Build multipart/form-data
      const payload = new FormData();
      for (const key in formData) {
        if (key !== "profileImage" && formData[key]) {
          payload.append(key, formData[key]);
        }
      }
      if (formData.profileImage) {
        payload.append("profileImage", formData.profileImage);
      }

      const response = await api.post(`${apiPrefix}/staff/new`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!response) {
        throw new Error("No response from server");
      }

      if (!response.success) {
        // Handle known messages from backend
        const msg = response.message || "Failed to add staff";
        const lowerMsg = msg.toLowerCase();
        
        if (lowerMsg.includes("exists_email") || lowerMsg.includes("exists email")) {
          toast.warn("Email already exists");
          setError("Email already exists");
        } else if (lowerMsg.includes("exists_mobile") || lowerMsg.includes("exists mobile")) {
          toast.warn("Mobile number already exists");
          setError("Mobile number already exists");
        } else {
          toast.error(msg);
          setError(msg);
        }
        setLoading(false);
        return;
      }

      // success
      toast.success(response.message || "Staff member added successfully!");
      setSuccess(response.message || "Staff member added successfully!");

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        mobile: "",
        email: "",
        password: "",
        restaurantId: "",
        roleId: "",
        profileImage: null,
      });
      setPreview(null);

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate(`${routePrefix}/staff`);
      }, 1200);
    } catch (error) {
      console.error("Error adding staff:", error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to add staff";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            className="text-2xl text-gray-600 hover:text-gray-800 mr-4"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Add New Staff Member</h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700 text-sm">{success}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6" autoComplete="off">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                placeholder="Enter first name"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="off"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                placeholder="Enter last name"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <input
                type="tel"
                name="mobile"
                placeholder="Enter mobile number"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.mobile}
                onChange={handleChange}
                autoComplete="off"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                placeholder="Enter email (optional)"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.email}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter password (staff can login with this)"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                value={formData.password}
                onChange={handleChange}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Staff can login using their mobile/email and this password
            </p>
          </div>

          {/* Business/Branch Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign to Business/Branch *
            </label>
            <select
              name="restaurantId"
              className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.restaurantId}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Business/Branch --</option>
              {data.restaurants.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.business_name} {restaurant.parent_id ? "(Branch)" : "(Business)"}
                </option>
              ))}
            </select>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign Role *
            </label>
            <select
              name="roleId"
              className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.roleId}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Role --</option>
              {data.roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.role_name || role.name}
                </option>
              ))}
            </select>
          </div>

          {/* Profile Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Image
            </label>
            <input
              type="file"
              name="profileImage"
              accept="image/*"
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {preview && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Preview:</p>
                <img
                  src={preview}
                  alt="Preview"
                  className="w-32 h-32 rounded-lg object-cover border border-gray-200"
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`${routePrefix}/staff`)}
              className="flex-1 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? "Adding..." : "Add Staff Member"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStaff;
